import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, RefreshCw, Wifi, WifiOff, Loader, ChevronRight,
  Droplets, X, Activity, Terminal, Trash2, Signal, Radio, Box, Lock, AlertTriangle
} from 'lucide-react';
import DeviceStatus from './DeviceStatus';
import { useDeviceKeys } from '../useDeviceKeys';
import { generateK1 } from '../cryptoUtils';
import { useMqttProxy } from '../mqttProxy';
import { aesDecrypt, hexToString } from '../cryptoUtils';

// TAM config (mirrors DeviceStatus.js)
const TAM_CONFIG = {
  '1': { title:'Pump Tamper Detected',       badge:'PUMP TAMPER',        color:'#FF3B30', bg:'linear-gradient(135deg,#FF3B30,#CC0000)', shadow:'rgba(255,59,48,.4)'  },
  '2': { title:'Flow Sensor Tamper Detected', badge:'FLOW SENSOR TAMPER', color:'#FF9F0A', bg:'linear-gradient(135deg,#FF9F0A,#CC7A00)', shadow:'rgba(255,159,10,.4)' },
};
const RESP_TOPIC_FOR = (mac) => `/hoags/livpure/lotier/waas/response/${mac.toLowerCase().replace(/[^a-f0-9]/g,'')}/`;
const TAM_HISTORY_KEY = (mac) => `hoags_tam_history_${mac}`;

// ── Last-online tracking (persisted in localStorage, keyed by MAC) ───────
// Updated every time a device successfully ACKs a ping. Used to flag
// devices that haven't come online in 7+ days — "Inactive" devices.
const LAST_ONLINE_KEY = 'hoags_last_online';
const INACTIVE_DAYS_THRESHOLD = 7;

function getLastOnlineMap() {
  try { return JSON.parse(localStorage.getItem(LAST_ONLINE_KEY) || '{}'); }
  catch { return {}; }
}
function markDeviceOnlineNow(mac) {
  try {
    const map = getLastOnlineMap();
    map[mac.toUpperCase()] = new Date().toISOString();
    localStorage.setItem(LAST_ONLINE_KEY, JSON.stringify(map));
  } catch(e) {}
}
function daysSinceLastOnline(mac) {
  const map = getLastOnlineMap();
  const iso = map[mac.toUpperCase()];
  if (!iso) return null; // never recorded — unknown, not counted as inactive
  const diffMs = Date.now() - new Date(iso).getTime();
  return diffMs / (1000 * 60 * 60 * 24);
}
function isDeviceInactive(mac) {
  const days = daysSinceLastOnline(mac);
  return days !== null && days >= INACTIVE_DAYS_THRESHOLD;
}
function saveTamEvent(mac, tamCode, deviceName) {
  try {
    const hist = JSON.parse(localStorage.getItem(TAM_HISTORY_KEY(mac)) || '[]');
    const cfg = TAM_CONFIG[tamCode]; if (!cfg) return;
    // UPSERT — if an entry for this tamCode already exists (acknowledged or not)
    // just update its detectedAt timestamp; never add a duplicate row.
    const existing = hist.find(h => h.tamCode === tamCode);
    if (existing) {
      existing.detectedAt = new Date().toISOString();
      // If it was already acknowledged, reset so the new alert shows again
      existing.acknowledgedAt = null;
    } else {
      hist.unshift({ id:Date.now(), tamCode, type:cfg.badge, title:cfg.title,
        device:deviceName, mac, detectedAt:new Date().toISOString(), acknowledgedAt:null });
    }
    localStorage.setItem(TAM_HISTORY_KEY(mac), JSON.stringify(hist.slice(0, 200)));
  } catch(e) {}
}

const APPLE_STYLES = `
  @keyframes spin      { to { transform:rotate(360deg); } }
  @keyframes dotPulse  { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.3;transform:scale(.6)} }
  @keyframes pulse     { 0%,100%{opacity:1} 50%{opacity:.4} }
  @keyframes fadeUp    { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes ringPing  { 0%{transform:scale(1);opacity:.7} 100%{transform:scale(2.4);opacity:0} }
  @keyframes slideIn   { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
  @keyframes hudIn     { from{opacity:0;transform:translate(-50%,-50%) scale(.7)} to{opacity:1;transform:translate(-50%,-50%) scale(1)} }
  @keyframes iosSpinBar{ from{opacity:1} to{opacity:.15} }
  .dcard { transition:box-shadow .22s ease,transform .22s ease; }
  .dcard:hover { box-shadow:0 10px 36px rgba(0,0,0,.13)!important; transform:translateY(-2px); }
  .abtn  { transition:all .18s ease; }
  .abtn:hover:not(:disabled){ transform:scale(1.04); }
  .abtn:active:not(:disabled){ transform:scale(.97); }
  .asearch input:focus { box-shadow:0 0 0 3px rgba(0,122,255,.18)!important; border-color:#007AFF!important; }
  .scard { transition:box-shadow .22s,transform .22s; }
  .scard:hover { transform:translateY(-2px); box-shadow:0 8px 28px rgba(0,0,0,.1)!important; }
`;

const API_BASE = '';  // Relative URL — works on EC2/havells.hoags.in
const PING_TIMEOUT_MS = 10000;
const PROGRESS_DURATION = 10;

const macToLower   = (mac) => mac.toLowerCase();
const publishTopic = (custname, mac, friendly) =>
  `hoags/${custname}/${macToLower(mac)}/${friendly}`;
const ackTopic     = (custname, mac, friendly) =>
  `hoags/${custname}/${macToLower(mac)}/${friendly}Ack`;

const normaliseDevice = (d) => ({
  friendlyname: d.friendly_name  ?? d.friendlyname  ?? '',
  prodmodel:    d.model_no       ?? d.prodmodel      ?? '',
  macid:        d.mac_id         ?? d.macid          ?? '',
  prodtype:     d.prod_type      ?? d.prodtype       ?? '',
  custname:     d.cust_name      ?? d.custname       ?? '',
  userid:       d.user_name      ?? d.userid         ?? '',
  version:      d.version_no     ?? '',
  _status: 'inactive',
  _keyStatus: 'none',
});

const deduplicateDevices = (arr) => {
  const seen = new Set();
  return arr.filter(d => {
    const key = d.macid.toUpperCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

function IOSSpinner() {
  return (<div style={{position:'relative',width:36,height:36}}>
    {Array.from({length:12}).map((_,i)=>(<div key={i} style={{position:'absolute',top:'50%',left:'50%',width:3,height:9,borderRadius:2,background:'#fff',transformOrigin:'50% 150%',transform:`rotate(${i*30}deg) translateX(-50%)`,animation:`iosSpinBar 1s linear ${-(12-i)/12}s infinite`}}/>))}
  </div>);
}
function IOSHud({ visible, label }) {
  if (!visible) return null;
  return (<div style={{position:'fixed',inset:0,zIndex:9999,pointerEvents:'none'}}>
    <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',background:'rgba(30,30,32,0.88)',backdropFilter:'blur(20px) saturate(180%)',WebkitBackdropFilter:'blur(20px) saturate(180%)',borderRadius:22,padding:'28px 32px',minWidth:130,display:'flex',flexDirection:'column',alignItems:'center',gap:16,boxShadow:'0 20px 60px rgba(0,0,0,0.5)',animation:'hudIn .22s cubic-bezier(.34,1.56,.64,1) both'}}>
      <IOSSpinner/><span style={{color:'#fff',fontSize:13,fontWeight:600,letterSpacing:.2,textAlign:'center',opacity:.9}}>{label}</span>
    </div></div>);
}

const LOG_TYPES = {
  api_req:{label:'API REQ',bg:'rgba(0,122,255,.15)',color:'#007AFF'},
  api_res:{label:'API RES',bg:'rgba(52,199,89,.15)',color:'#34C759'},
  api_err:{label:'API ERR',bg:'rgba(255,59,48,.15)',color:'#FF3B30'},
  mqtt_pub:{label:'MQTT PUB',bg:'rgba(255,159,10,.15)',color:'#FF9F0A'},
  mqtt_sub:{label:'MQTT SUB',bg:'rgba(175,82,222,.15)',color:'#AF52DE'},
  mqtt_ack:{label:'MQTT ACK',bg:'rgba(52,199,89,.15)',color:'#34C759'},
  mqtt_timeout:{label:'TIMEOUT',bg:'rgba(142,142,147,.15)',color:'#8E8E93'},
  crypto_hs:{label:'HANDSHAKE',bg:'rgba(175,82,222,.15)',color:'#AF52DE'},
  crypto_ok:{label:'CRYPTO OK',bg:'rgba(52,199,89,.15)',color:'#34C759'},
  crypto_err:{label:'CRYPTO ER',bg:'rgba(255,59,48,.15)',color:'#FF3B30'},
};

function DebugPanel({ logs, onClear }) {
  const [open, setOpen] = useState(false);
  const bottomRef = useRef(null);
  useEffect(() => { if (open) bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [logs, open]);
  return (
    <div style={{borderRadius:16,marginBottom:20,overflow:'hidden',background:'rgba(15,23,42,0.96)',border:'1px solid rgba(255,255,255,0.07)',boxShadow:'0 4px 20px rgba(0,0,0,0.15)'}}>
      <div onClick={()=>setOpen(o=>!o)} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 16px',cursor:'pointer',userSelect:'none'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <Terminal size={13} color="#007AFF"/>
          <span style={{fontSize:12,fontWeight:600,color:'#e2e8f0',letterSpacing:.2}}>Debug Console</span>
          {logs.length>0&&<span style={{fontSize:10,background:'rgba(0,122,255,.2)',color:'#007AFF',padding:'1px 8px',borderRadius:20,fontWeight:600}}>{logs.length}</span>}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <button onClick={e=>{e.stopPropagation();onClear();}} style={{background:'rgba(255,255,255,.06)',border:'none',borderRadius:6,padding:'3px 9px',cursor:'pointer',color:'#8E8E93',fontSize:11,display:'flex',alignItems:'center',gap:4}}><Trash2 size={10}/> Clear</button>
          <span style={{color:'#3a3a3c',fontSize:11}}>{open?'▲':'▼'}</span>
        </div>
      </div>
      {open&&(<div style={{maxHeight:240,overflowY:'auto',fontFamily:'monospace'}}>
        {logs.length===0&&<div style={{padding:'20px',color:'#3a3a3c',fontSize:12,textAlign:'center'}}>No events yet…</div>}
        {logs.map((log,i)=>{const t=LOG_TYPES[log.type]||{label:log.type,bg:'rgba(255,255,255,.1)',color:'#fff'};return(
          <div key={i} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'5px 16px',borderBottom:'1px solid rgba(255,255,255,.04)'}}>
            <span style={{color:'#48484a',fontSize:10,minWidth:58,paddingTop:2,flexShrink:0}}>{log.time}</span>
            <span style={{fontSize:9,fontWeight:700,padding:'2px 7px',borderRadius:5,background:t.bg,color:t.color,minWidth:70,textAlign:'center',flexShrink:0}}>{t.label}</span>
            <span style={{fontSize:11,color:'#aeaeb2',wordBreak:'break-all',lineHeight:1.6}}>{log.msg}</span>
          </div>);})}
        <div ref={bottomRef}/>
      </div>)}
    </div>);
}

function PingProgressBar({ pinging, onDone }) {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null); const startRef = useRef(null);
  useEffect(() => {
    if (!pinging) { setProgress(0); return; }
    startRef.current = performance.now();
    const tick = (now) => { const pct = Math.min(((now-startRef.current)/1000/PROGRESS_DURATION)*100,100); setProgress(pct); if (pct<100) rafRef.current=requestAnimationFrame(tick); else onDone&&onDone(); };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [pinging, onDone]);
  if (!pinging && progress===0) return null;
  const remaining = Math.max(0,Math.ceil(PROGRESS_DURATION-(progress/100)*PROGRESS_DURATION));
  return (
    <div style={{background:'rgba(255,159,10,.07)',border:'1px solid rgba(255,159,10,.22)',borderRadius:14,padding:'14px 18px',marginBottom:16,animation:'slideIn .3s ease'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
        <div style={{display:'flex',alignItems:'center',gap:8,fontSize:13,fontWeight:600,color:'#FF9F0A'}}><Activity size={14} style={{animation:'pulse 1s ease-in-out infinite'}}/>Scanning for responses</div>
        <span style={{fontSize:12,color:'#FF9F0A',fontWeight:600,background:'rgba(255,159,10,.12)',padding:'2px 10px',borderRadius:20}}>{remaining}s</span>
      </div>
      <div style={{background:'rgba(255,159,10,.15)',borderRadius:999,height:5,overflow:'hidden'}}>
        <div style={{height:'100%',borderRadius:999,background:'linear-gradient(90deg,#FF9F0A,#FF3B30)',width:`${progress}%`,transition:'width .1s linear',boxShadow:'0 0 8px rgba(255,159,10,.5)'}}/>
      </div>
      <div style={{fontSize:11,color:'rgba(255,159,10,.6)',marginTop:8}}>📡 Ping sent · Listening on ACK topics · Up to {PROGRESS_DURATION}s</div>
    </div>);
}

function KeyStatusBadge({ status }) {
  const map = {
    none:{bg:'rgba(142,142,147,.1)',color:'#8E8E93',label:'No Key'},
    pending:{bg:'rgba(255,159,10,.12)',color:'#FF9F0A',label:'Getting Key…'},
    success:{bg:'rgba(52,199,89,.12)',color:'#34C759',label:'🔐 Encrypted'},
    failed:{bg:'rgba(255,59,48,.12)',color:'#FF3B30',label:'Key Failed'},
  };
  const s = map[status]||map.none;
  return (<span style={{display:'inline-flex',alignItems:'center',gap:4,padding:'2px 8px',borderRadius:16,fontSize:10,fontWeight:600,background:s.bg,color:s.color}}>
    {status==='pending'&&<Loader size={8} style={{animation:'spin 1s linear infinite'}}/>}
    {status==='success'&&<Lock size={8}/>}{s.label}</span>);
}

function StatusPill({ status }) {
  const map = {
    active:{bg:'rgba(52,199,89,.12)',color:'#34C759',label:'Online',pulse:true},
    inactive:{bg:'rgba(142,142,147,.1)',color:'#8E8E93',label:'Offline',pulse:false},
    pinging:{bg:'rgba(255,159,10,.12)',color:'#FF9F0A',label:'Scanning',pulse:true},
  };
  const s = map[status]||map.inactive;
  return (<span style={{display:'inline-flex',alignItems:'center',gap:5,padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:600,background:s.bg,color:s.color}}>
    <span style={{width:5,height:5,borderRadius:'50%',background:s.color,animation:s.pulse?'dotPulse 1.4s ease-in-out infinite':'none'}}/>
    {status==='pinging'&&<Loader size={9} style={{animation:'spin .8s linear infinite'}}/>}{s.label}</span>);
}

function DeviceCard({ device, onClick, index }) {
  const isActive=device._status==='active'; const isPinging=device._status==='pinging';
  return (
    <div className="dcard" onClick={()=>onClick(device)} style={{background:'#fff',borderRadius:18,padding:'18px 22px',display:'flex',alignItems:'center',gap:16,cursor:'pointer',boxShadow:'0 2px 12px rgba(0,0,0,0.06)',border:isActive?'1px solid rgba(52,199,89,.2)':'1px solid rgba(0,0,0,0.06)',animation:'fadeUp .35s ease both',animationDelay:`${index*0.06}s`,position:'relative',overflow:'hidden'}}>
      {isActive&&<div style={{position:'absolute',left:0,top:0,bottom:0,width:3,background:'linear-gradient(180deg,#34C759,#30D158)',borderRadius:'3px 0 0 3px'}}/>}
      <div style={{width:46,height:46,borderRadius:13,flexShrink:0,position:'relative',overflow:'hidden',boxShadow:isActive?'0 4px 14px rgba(52,199,89,.35)':isPinging?'0 4px 14px rgba(255,159,10,.3)':'0 2px 8px rgba(0,0,0,.1)',border:isActive?'2px solid rgba(52,199,89,.4)':isPinging?'2px solid rgba(255,159,10,.4)':'2px solid rgba(0,0,0,.08)'}}>
        <img src="/havellspurifier.jpg" alt="Havells" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
        {isPinging&&<div style={{position:'absolute',inset:-4,borderRadius:17,border:'2px solid rgba(255,159,10,.5)',animation:'ringPing 1.2s ease-out infinite'}}/>}
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:5}}>
          <span style={{fontWeight:700,fontSize:15,color:'#1c1c1e',letterSpacing:-.2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{device.friendlyname}</span>
          <StatusPill status={device._status}/>
          {isActive&&<KeyStatusBadge status={device._keyStatus}/>}
        </div>
        <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>
          <span style={{fontSize:11,color:'#8E8E93',display:'flex',alignItems:'center',gap:3}}><Box size={10}/> {device.prodmodel}</span>
          <span style={{fontSize:11,color:'#8E8E93',fontFamily:'monospace'}}>{device.macid.toUpperCase()}</span>
          <span style={{fontSize:11,color:'#8E8E93',display:'flex',alignItems:'center',gap:3}}><Signal size={10}/> {device.custname === 'Livpure' ? 'Havells' : device.custname}</span>
        </div>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
        <span style={{fontSize:10,fontWeight:700,letterSpacing:.6,padding:'3px 9px',borderRadius:8,background:'rgba(0,122,255,.1)',color:'#007AFF',textTransform:'uppercase'}}>{device.prodtype}</span>
        {isActive&&<ChevronRight size={15} color="#C7C7CC"/>}
      </div>
    </div>);
}

function DeviceModal({ device, onClose, onViewStatus }) {
  if (!device) return null;
  const isActive = device._status==='active';
  const rows = [['Friendly Name',device.friendlyname,false],['Model',device.prodmodel,false],['Version',device.version,false],['MAC ID',device.macid.toUpperCase(),true],['Type',device.prodtype?.toUpperCase(),false],['Customer',device.custname === 'Livpure' ? 'Havells' : device.custname,false]];
  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',backdropFilter:'blur(14px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200,animation:'fadeUp .2s ease'}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'rgba(255,255,255,0.97)',backdropFilter:'blur(40px)',borderRadius:24,padding:28,width:480,maxWidth:'95vw',boxShadow:'0 32px 80px rgba(0,0,0,0.2)',border:'1px solid rgba(255,255,255,0.6)',animation:'fadeUp .25s ease'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:22}}>
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <div style={{width:52,height:52,borderRadius:16,overflow:'hidden',flexShrink:0,boxShadow:isActive?'0 6px 20px rgba(52,199,89,.4)':'0 2px 8px rgba(0,0,0,.1)',border:isActive?'2px solid rgba(52,199,89,.4)':'2px solid rgba(0,0,0,.08)'}}><img src="/havellspurifier.jpg" alt="Havells" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/></div>
            <div><div style={{fontWeight:700,fontSize:18,color:'#1c1c1e',letterSpacing:-.3}}>{device.friendlyname}</div><StatusPill status={device._status}/></div>
          </div>
          <button onClick={onClose} style={{width:28,height:28,borderRadius:'50%',border:'none',background:'rgba(0,0,0,0.07)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}><X size={13} color="#48484a"/></button>
        </div>
        <div style={{display:'grid',gap:6,marginBottom:16}}>
          {rows.map(([label,value,mono])=>(<div key={label} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:'rgba(0,0,0,0.03)',borderRadius:10}}>
            <span style={{fontSize:12,color:'#8E8E93',fontWeight:500}}>{label}</span>
            <span style={{fontSize:12,color:'#1c1c1e',fontWeight:600,fontFamily:mono?'monospace':'inherit'}}>{value}</span></div>))}
        </div>
        <div style={{marginBottom:20}}/>
        <div style={{display:'flex',gap:10}}>
          <button onClick={onClose} style={{flex:1,padding:13,borderRadius:14,border:'1px solid rgba(0,0,0,.1)',background:'#fff',color:'#1c1c1e',fontWeight:600,fontSize:14,cursor:'pointer',fontFamily:'inherit'}}>Close</button>
          {onViewStatus&&(<button onClick={()=>onViewStatus(device)} style={{flex:2,padding:13,borderRadius:14,border:'none',background:isActive?'linear-gradient(135deg,#007AFF,#0063CC)':'linear-gradient(135deg,#8E8E93,#636366)',color:'#fff',fontWeight:600,fontSize:14,cursor:'pointer',letterSpacing:.2,fontFamily:'inherit',boxShadow:isActive?'0 4px 14px rgba(0,122,255,.3)':'none'}}>
            {isActive?'📊 View Device Status':'📋 View Last Data'}</button>)}
        </div>
      </div>
    </div>);
}

// ── Global Tamper Alert Banner ────────────────────────────────────────────
function TamAlertBanner({ alerts, onDismiss, onGoToDevice }) {
  if (alerts.length === 0) return null;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:18 }}>
      {alerts.map((alert, i) => {
        const cfg = TAM_CONFIG[alert.tamCode];
        if (!cfg) return null;
        return (
          <div key={`${alert.mac}-${alert.tamCode}`} style={{
            background:`linear-gradient(135deg,${cfg.color}12,${cfg.color}06)`,
            border:`1.5px solid ${cfg.color}40`,
            borderRadius:16, padding:'14px 18px',
            display:'flex', alignItems:'center', gap:14,
            animation:'fadeUp .3s ease',
            boxShadow:`0 4px 20px ${cfg.shadow}20`,
          }}>
            {/* Pulsing icon */}
            <div style={{
              width:44, height:44, borderRadius:13, flexShrink:0,
              background:cfg.bg,
              display:'flex', alignItems:'center', justifyContent:'center',
              boxShadow:`0 4px 14px ${cfg.shadow}`,
              animation:'tamGlobalPulse 1.8s ease-in-out infinite',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 19h20L12 2Z" fill="white" fillOpacity=".25"
                  stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M12 9v5" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                <circle cx="12" cy="17" r="1.2" fill="white"/>
              </svg>
            </div>
            {/* Text */}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                <span style={{
                  fontSize:9, fontWeight:800, padding:'2px 8px', borderRadius:20,
                  background:`${cfg.color}18`, color:cfg.color, letterSpacing:.8,
                }}>{cfg.badge}</span>
                <span style={{ fontSize:12, color:'#8E8E93' }}>
                  {new Date(alert.detectedAt).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true})}
                </span>
              </div>
              <div style={{ fontSize:13, fontWeight:700, color:'#1c1c1e', marginBottom:2 }}>
                {cfg.title}
              </div>
              <div style={{ fontSize:11, color:'#8E8E93' }}>
                Device: <strong>{alert.deviceName}</strong> · {alert.mac.toUpperCase()}
              </div>
            </div>
            {/* Actions */}
            <div style={{ display:'flex', gap:8, flexShrink:0 }}>
              <button onClick={()=>onGoToDevice(alert)} style={{
                padding:'7px 14px', borderRadius:10, border:'none',
                background:cfg.bg, color:'#fff',
                fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                boxShadow:`0 2px 8px ${cfg.shadow}`,
              }}>View Device</button>
              <button onClick={()=>onDismiss(alert.mac)} style={{
                width:28, height:28, borderRadius:'50%', border:'none',
                background:'rgba(0,0,0,.07)', color:'#636366',
                cursor:'pointer', display:'flex', alignItems:'center',
                justifyContent:'center', fontSize:14, flexShrink:0,
              }}>×</button>
            </div>
          </div>
        );
      })}
      <style>{`
        @keyframes tamGlobalPulse {
          0%,100% { transform:scale(1); }
          50%      { transform:scale(1.06); }
        }
      `}</style>
    </div>
  );
}

export default function DeviceList({ userId, isAdmin, role, session }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [isPinging, setIsPinging] = useState(false);
  const timersRef = useRef({});
  const { connected, subscribe, publish } = useMqttProxy();
  const { handshake, hasKey, getK2 } = useDeviceKeys();
  const handshakeRanRef = useRef(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [statusDevice, setStatusDevice] = useState(null);
  const [logs, setLogs] = useState([]);
  const [hudLabel, setHudLabel] = useState('');
  const [tamAlerts, setTamAlerts] = useState([]); // global tamper alerts
  const tamSeenRef = useRef(new Set()); // avoid duplicate alerts same session

  const addLog = useCallback((type, msg) => {
    const time = new Date().toLocaleTimeString('en-GB', { hour12: false });
    console.log(`[${time}] [${type}]`, msg);
    setLogs(prev => [...prev.slice(-199), { type, msg, time }]);
  }, []);

  const fetchDevices = useCallback(async () => {
    if (!isAdmin && !userId) { addLog('api_err','userId null'); setFetchError('No user session.'); setLoading(false); return; }
    setLoading(true); setHudLabel('Loading Devices…'); setFetchError('');
    handshakeRanRef.current = false;
    try {
      const url = `${API_BASE}/api/admin-devices`;
      addLog('api_req', `GET ${url}`);
      const res = await fetch(url, { headers:{'ngrok-skip-browser-warning':'true'} });
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const raw = await res.json();
      const data = Array.isArray(raw)?raw:(raw.registrations??raw.data??raw.devices??[]);
      addLog('api_res', `✅ ${data.length} device(s)`);
      const unique = deduplicateDevices(data.map(normaliseDevice));
      addLog('api_res', `📦 ${unique.length} unique`);
      setDevices(unique);
    } catch (e) { addLog('api_err',`❌ ${e.message}`); setFetchError(e.message); }
    finally { setLoading(false); setHudLabel(''); }
  }, [userId, isAdmin, addLog]);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const pingAll = useCallback(() => {
    if (!connected || devices.length === 0) return;
    setIsPinging(true); setHudLabel('Pinging Devices…');
    handshakeRanRef.current = false;
    devices.forEach(device => {
      const pub = publishTopic(device.custname, device.macid, device.friendlyname);
      const ack = ackTopic(device.custname, device.macid, device.friendlyname);
      addLog('mqtt_sub', `Subscribed → ${ack}`);
      setDevices(prev => prev.map(d => d.macid===device.macid?{...d,_status:'pinging'}:d));

      const unsub = subscribe(ack, (message) => {
        // Normalise — could be string, Buffer, or object
        const msgStr = typeof message === 'string'
          ? message
          : (message instanceof Uint8Array)
            ? new TextDecoder().decode(message)
            : JSON.stringify(message);
        const msgLower = msgStr.trim().toLowerCase();
        addLog('mqtt_ack', `✅ ACK received [${device.friendlyname}]: "${msgStr.slice(0,30)}"`);
        if (msgLower === 'ack' || msgLower.includes('ack')) {
          clearTimeout(timersRef.current[device.macid]);
          markDeviceOnlineNow(device.macid);
          setDevices(prev => prev.map(d => d.macid===device.macid?{...d,_status:'active'}:d));
          unsub();
        }
      });

      // Small delay to ensure subscription is registered before publishing
      setTimeout(() => {
        addLog('mqtt_pub', `Published "ping" → ${pub}`);
        publish(pub, 'ping');
      }, 200);

      timersRef.current[device.macid] = setTimeout(() => {
        addLog('mqtt_timeout', `⏱ No ACK for [${device.friendlyname}]`);
        setDevices(prev => prev.map(d => d.macid===device.macid&&d._status==='pinging'?{...d,_status:'inactive'}:d));
        unsub(); setHudLabel('');
      }, PING_TIMEOUT_MS);
    });
  }, [connected, devices, subscribe, publish, addLog]);

  useEffect(() => {
    if (isPinging || loading || devices.length === 0) return;
    if (handshakeRanRef.current) return;
    const stillPinging = devices.some(d => d._status === 'pinging');
    if (stillPinging) return;
    const onlineDevices = devices.filter(d => d._status === 'active');
    if (onlineDevices.length === 0) return;
    handshakeRanRef.current = true;
    addLog('crypto_hs', `🤝 Handshake for ${onlineDevices.length} device(s)`);
    setHudLabel('Getting Encryption Keys…');
    setDevices(prev => prev.map(d =>
      onlineDevices.find(od => od.macid === d.macid) ? { ...d, _keyStatus: 'pending' } : d
    ));
    (async () => {
      let succeeded = 0;
      for (const dev of onlineDevices) {
        const ok = await handshake(dev.macid, dev.macid);
        if (ok) succeeded++;
        setDevices(prev => prev.map(d =>
          d.macid === dev.macid ? { ...d, _keyStatus: ok ? 'success' : 'failed' } : d
        ));
      }
      addLog('crypto_ok', `✅ Handshake done: ${succeeded}/${onlineDevices.length}`);
      setHudLabel('');
    })();
  }, [isPinging, loading, devices, handshake, addLog]);

  useEffect(() => {
    if (!loading && devices.length > 0 && connected) pingAll();
  }, [loading, connected]); // eslint-disable-line

  useEffect(() => { return () => Object.values(timersRef.current).forEach(clearTimeout); }, []);

  // ── Global TAM listener — subscribe to all device response topics ────────────
  useEffect(() => {
    if (!connected || devices.length === 0) return;
    const unsubs = devices.map(device => {
      const respTopic = RESP_TOPIC_FOR(device.macid);
      return subscribe(respTopic, async (message) => {
        try {
          const raw = typeof message === 'string' ? message : JSON.stringify(message);
          let json;
          try { json = JSON.parse(raw); } catch { return; }

          // Try to decrypt VAL if encrypted
          // Only decrypt if VAL is long hex (>20 chars) — short VAL like "1","2" is plaintext
          if (json.VAL && /^[0-9a-fA-F]+$/.test(json.VAL) && json.VAL.length > 20) {
            const k2 = getK2 ? getK2(device.macid) : null;
            if (k2) {
              try {
                const decHex = await aesDecrypt(json.VAL, k2);
                json = JSON.parse(hexToString(decHex));
              } catch { /* not decryptable — skip */ return; }
            } else { return; } // no key yet
          }

          if (json.CMD !== 'TAM') return;

          const tamCode = String(json.VAL);
          const seenKey = `${device.macid}-${tamCode}`;
          if (!TAM_CONFIG[tamCode] || tamSeenRef.current.has(seenKey)) return;
          tamSeenRef.current.add(seenKey);

          // Save to localStorage history
          saveTamEvent(device.macid, tamCode, device.friendlyname);

          // Add to dashboard alerts
          setTamAlerts(prev => {
            const exists = prev.find(a => a.mac === device.macid && a.tamCode === tamCode);
            if (exists) return prev;
            return [{
              mac: device.macid,
              deviceName: device.friendlyname,
              tamCode,
              detectedAt: new Date().toISOString(),
            }, ...prev];
          });

          addLog('mqtt_ack', `⚠️ TAM [${tamCode}] on ${device.friendlyname}`);
        } catch(e) {}
      });
    });
    return () => unsubs.forEach(u => u && u());
  }, [connected, devices, subscribe, getK2, addLog]); // eslint-disable-line

  useEffect(() => {
    if (isPinging && devices.length > 0 && devices.every(d => d._status !== 'pinging')) {
      setIsPinging(false); setHudLabel('');
    }
  }, [devices, isPinging]);

  const filtered = devices
    .filter(d => d.friendlyname.toLowerCase().includes(search.toLowerCase()) || d.macid.toLowerCase().includes(search.toLowerCase()) || d.custname.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b) => {const o={active:0,pinging:1,inactive:2};return(o[a._status]??2)-(o[b._status]??2);});

  const counts = {
    total:devices.length, active:devices.filter(d=>d._status==='active').length,
    inactive:devices.filter(d=>d._status==='inactive').length,
    pinging:devices.filter(d=>d._status==='pinging').length,
    encrypted:devices.filter(d=>d._keyStatus==='success').length,
    dormant:devices.filter(d=>isDeviceInactive(d.macid)).length,
  };

  if (statusDevice) return <DeviceStatus device={statusDevice} onBack={()=>setStatusDevice(null)} getK2={getK2}/>;

  return (
    <div style={{fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif"}}>
      <style>{APPLE_STYLES}</style>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:16,marginBottom:28}}>
        <div><img src="/havells-logo.png" alt="Havells" style={{height:48,objectFit:'contain',marginBottom:10,display:'block'}}/>
          <h1 style={{fontSize:28,fontWeight:800,color:'#1c1c1e',letterSpacing:-.6,margin:0}}>Devices</h1>
          <p style={{color:'#8E8E93',fontSize:14,marginTop:4,fontWeight:400}}>Real-time device monitoring · MQTT + Encrypted</p></div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {/* Role badge — replaces MQTT status */}
          {(() => {
            // Only kishore is the true Super Admin. Ayesha and other
            // allowlisted admins show as 'Admin'.
            const SUPER_ADMINS = ['kishore@hoags.in'];
            const sessionEmail = (session?.getIdToken?.()?.payload?.email ||
              session?.getIdToken?.()?.payload?.['cognito:username'] || '').toLowerCase();
            const r = role || (isAdmin ? 'admin' : 'service_manager');
            const isSuperAdmin = r === 'admin' && SUPER_ADMINS.includes(sessionEmail);
            const ROLE_CFG = {
              super_admin:       { label:'Super Admin',     bg:'rgba(180,10,10,.12)', color:'#B91C1C', border:'rgba(180,10,10,.3)'   },
              admin:             { label:'Admin',           bg:'rgba(220,38,38,.1)',  color:'#DC2626', border:'rgba(220,38,38,.25)'  },
              service_manager:   { label:'Manager',         bg:'rgba(37,99,235,.1)', color:'#2563EB', border:'rgba(37,99,235,.25)'  },
              customer_engineer: { label:'Support Engineer',bg:'rgba(124,58,237,.1)',color:'#7C3AED', border:'rgba(124,58,237,.25)' },
            };
            const key = isSuperAdmin ? 'super_admin' : r;
            const cfg = ROLE_CFG[key] || ROLE_CFG.admin;
            return (
              <div style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 14px',
                borderRadius:20,fontSize:12,fontWeight:700,
                background:cfg.bg,color:cfg.color,border:`1px solid ${cfg.border}`}}>
                <span style={{width:6,height:6,borderRadius:'50%',background:cfg.color}}/>
                {cfg.label}
              </div>
            );
          })()}
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))',gap:14,marginBottom:24}}>
        {[{label:'Total',value:counts.total,color:'#007AFF',Icon:Droplets,glow:false},{label:'Online',value:counts.active,color:'#34C759',Icon:Wifi,glow:counts.active>0},{label:'Offline',value:counts.inactive,color:'#8E8E93',Icon:WifiOff,glow:false},{label:'Encrypted',value:counts.encrypted,color:'#AF52DE',Icon:Lock,glow:counts.encrypted>0},{label:'Dormant (7d+)',value:counts.dormant,color:'#FF3B30',Icon:AlertTriangle,glow:counts.dormant>0}].map(({label,value,color,Icon,glow})=>(
          <div key={label} className="scard" style={{background:'#fff',borderRadius:18,padding:'18px 20px',boxShadow:glow?`0 4px 20px ${color}28`:'0 2px 10px rgba(0,0,0,.06)',border:`1px solid ${glow?color+'22':'rgba(0,0,0,.06)'}`}}>
            <div style={{width:38,height:38,borderRadius:10,marginBottom:12,background:`linear-gradient(135deg,${color}20,${color}10)`,display:'flex',alignItems:'center',justifyContent:'center'}}><Icon size={18} color={color} style={(label==='Encrypted'||label==='Dormant (7d+)')&&glow?{animation:'pulse 1s ease-in-out infinite'}:{}}/></div>
            <div style={{fontSize:26,fontWeight:800,color:'#1c1c1e',letterSpacing:-.5,display:'flex',alignItems:'center',gap:6,marginBottom:2}}>{glow&&<span style={{width:7,height:7,borderRadius:'50%',background:color,animation:'dotPulse 1.2s ease-in-out infinite'}}/>}{value}</div>
            <div style={{fontSize:12,color:'#8E8E93',fontWeight:500}}>{label}</div></div>))}
      </div>
      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <div className="asearch" style={{flex:1,minWidth:220,position:'relative'}}>
          <Search size={14} style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'#8E8E93'}}/>
          <input placeholder="Search devices…" value={search} onChange={e=>setSearch(e.target.value)}
            style={{width:'100%',padding:'10px 14px 10px 36px',borderRadius:12,border:'1px solid rgba(0,0,0,.1)',fontSize:13,background:'#fff',outline:'none',boxSizing:'border-box',color:'#1c1c1e',boxShadow:'0 1px 4px rgba(0,0,0,.06)',transition:'box-shadow .2s,border-color .2s'}}/>
        </div>
        <button className="abtn" onClick={()=>{setIsPinging(false);fetchDevices();}} disabled={loading}
          style={{display:'inline-flex',alignItems:'center',gap:6,padding:'10px 18px',borderRadius:12,border:'none',background:'linear-gradient(135deg,#007AFF,#0063CC)',color:'#fff',fontWeight:600,fontSize:13,cursor:'pointer',boxShadow:'0 4px 14px rgba(0,122,255,.28)',opacity:loading?.6:1}}>
          <RefreshCw size={13} style={loading?{animation:'spin .8s linear infinite'}:{}}/> Refresh</button>
        <button className="abtn" onClick={pingAll} disabled={!connected||loading||devices.length===0}
          style={{display:'inline-flex',alignItems:'center',gap:6,padding:'10px 18px',borderRadius:12,border:'1px solid rgba(0,0,0,.1)',background:'#fff',color:'#1c1c1e',fontWeight:600,fontSize:13,cursor:'pointer',boxShadow:'0 1px 4px rgba(0,0,0,.06)',opacity:(!connected||loading||devices.length===0)?.4:1}}>
          <Wifi size={13}/> Ping All</button>
      </div>
      <PingProgressBar pinging={isPinging} onDone={()=>setIsPinging(false)}/>
      {/* Global Tamper Alerts */}
      <TamAlertBanner
        alerts={tamAlerts}
        onDismiss={(mac) => setTamAlerts(prev => prev.filter(a => a.mac !== mac))}
        onGoToDevice={(alert) => {
          const device = devices.find(d => d.macid === alert.mac);
          if (device) {
            setSelected(null);
            setStatusDevice(device._status==='active' ? device : {...device, _isOffline:true});
          }
          setTamAlerts(prev => prev.filter(a => a.mac !== alert.mac));
        }}
      />
      {fetchError&&(<div style={{background:'rgba(255,59,48,.06)',border:'1px solid rgba(255,59,48,.15)',borderRadius:12,padding:'13px 18px',marginBottom:16,color:'#FF3B30',fontSize:13,animation:'slideIn .3s ease'}}>
        <strong>Error:</strong> {fetchError}</div>)}
      {loading?(<div style={{textAlign:'center',padding:'70px 0',color:'#8E8E93'}}>
        <div style={{width:36,height:36,borderRadius:'50%',border:'3px solid rgba(0,122,255,.15)',borderTopColor:'#007AFF',animation:'spin .7s linear infinite',margin:'0 auto 14px'}}/>
        <p style={{fontSize:13,fontWeight:500}}>Loading devices…</p></div>
      ):(<div style={{display:'flex',flexDirection:'column',gap:10}}>
          {filtered.map((d,i)=>(<DeviceCard key={d.macid} device={d} index={i} onClick={setSelected}/>))}
          {filtered.length===0&&(<div style={{textAlign:'center',padding:'60px 20px',background:'#fff',borderRadius:18,border:'1px solid rgba(0,0,0,.06)',boxShadow:'0 2px 10px rgba(0,0,0,.05)',color:'#8E8E93',fontSize:13}}>
            <Droplets size={32} color="#C7C7CC" style={{marginBottom:10}}/><p style={{fontWeight:500}}>No devices found.</p></div>)}
        </div>)}
      {selected&&<DeviceModal device={selected} onClose={()=>setSelected(null)} onViewStatus={(d)=>{
        setSelected(null);
        const deviceWithData = d._status==='active'?d:{...d,_isOffline:true};
        setStatusDevice(deviceWithData);
      }}/>}
      <IOSHud visible={!!hudLabel} label={hudLabel}/>
    </div>);
}
